<template>
    <Container>
        <div class="my-4 flex w-full items-center justify-between">
            <h1 class="text-2xl font-bold text-white">Edit Song</h1>
            <Link href="/backstage/songs"
                class="rounded-md bg-nomad-900 px-4 py-2 text-white transition-colors hover:bg-nomad-800">
                Cancel
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-5xl space-y-4">
            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Basic Information</h2>
                <div class="grid gap-4 md:grid-cols-2">
                    <div class="md:col-span-2">
                        <label for="song-name" class="mb-2 block text-sm font-medium text-nomad-300">
                            Song name <span class="text-red-400">*</span>
                        </label>
                        <input id="song-name" v-model="form.name" type="text" required
                            class="w-full rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Enter song name" />
                        <span v-if="form.errors.name" class="mt-1 block text-sm text-red-400">{{ form.errors.name }}</span>
                    </div>
                    <div>
                        <label for="song-release-year" class="mb-2 block text-sm font-medium text-nomad-300">Release year</label>
                        <input id="song-release-year" v-model="form.released" type="number"
                            class="w-full rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Year" />
                        <span v-if="form.errors.released" class="mt-1 block text-sm text-red-400">{{ form.errors.released }}</span>
                    </div>
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Playlist</label>
                        <Selector type="playlists" placeholder="Select playlist" v-model="form.playlists" max="10" />
                    </div>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Artists</h2>
                <div class="grid gap-4 md:grid-cols-2">
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">
                            Main artist <span class="text-red-400">*</span>
                        </label>
                        <Selector type="artists" v-model="form.artist" placeholder="Select artist" />
                        <span v-if="form.errors['artist.*.id']" class="mt-1 block text-sm text-red-400">
                            {{ form.errors['artist.*.id'] }}
                        </span>
                    </div>
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Original singer</label>
                        <Selector type="artists" v-model="form.original_artist" placeholder="Select original singer" />
                    </div>
                    <div class="md:col-span-2">
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Featuring artists</label>
                        <Selector type="artists" v-model="form.features" placeholder="Select featuring artists" max="4" />
                    </div>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Audio & Artwork</h2>
                <div class="space-y-5">
                    <div>
                        <label class="mb-2 block text-sm font-medium text-nomad-300">Audio file</label>
                        <AudioUploader v-model="form.audio" />
                    </div>
                    <div>
                        <label for="youtube-id" class="mb-2 block text-sm font-medium text-nomad-300">YouTube</label>
                        <input id="youtube-id" v-model="form.youtube" type="text"
                            class="w-full rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="YouTube ID or full URL" />
                        <span v-if="form.errors.youtube" class="mt-1 block text-sm text-red-400">{{ form.errors.youtube }}</span>
                    </div>
                    <YoutubeImage :youtube="form.youtube" v-model="form.cover" v-model:thumbnail="form.meta.thumbnail" />
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Processing Options</h2>
                <div class="space-y-4">
                    <label for="download-youtube-audio" class="flex items-center gap-3 text-nomad-300">
                        <input id="download-youtube-audio" v-model="form.meta.is_youtube" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Download audio from YouTube</span>
                    </label>
                    <label for="retry-audio-process" class="flex items-center gap-3 text-nomad-300">
                        <input id="retry-audio-process" v-model="form.meta.retry" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Retry audio process</span>
                    </label>
                    <label for="smart-music" class="flex items-center gap-3 text-nomad-300">
                        <input id="smart-music" v-model="form.meta.is_smart" type="checkbox"
                            class="rounded border-nomad-700 bg-nomad-800 text-nomad-accent focus:ring-nomad-accent" />
                        <span>Smart Music</span>
                    </label>
                </div>
            </section>

            <div class="flex flex-wrap gap-3 pb-6">
                <button type="submit" :disabled="form.processing"
                    class="rounded-md bg-nomad-accent px-6 py-3 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-60">
                    {{ form.processing ? 'Saving...' : 'Save Changes' }}
                </button>
                <button type="button" :disabled="deleting" @click="deleteSong"
                    class="ml-auto rounded-md bg-red-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {{ deleting ? 'Deleting...' : 'Delete Song' }}
                </button>
            </div>
        </form>
    </Container>
</template>

<script setup>
import { ref } from 'vue';
import { Link, router, useForm } from '@inertiajs/vue3';
import Container from '@/Pages/Backstage/Container.vue';
import Selector from '@/Pages/Backstage/Components/Selector.vue';
import YoutubeImage from '@/Pages/Backstage/Components/YoutubeImage.vue';
import AudioUploader from '@/Pages/Backstage/Components/AudioUploader.vue';

const props = defineProps({ song: { type: Object, required: true } });
const deleting = ref(false);

const form = useForm({
    name: props.song.name,
    artist: props.song.artist ? [props.song.artist] : [],
    original_artist: props.song.origin_artist ? [props.song.origin_artist] : [],
    features: props.song.featuring || [],
    playlists: props.song.playlists || [],
    released: props.song.released_at,
    youtube: props.song.youtube,
    lyrics: props.song.lyrics,
    cover: props.song.cover,
    audio: props.song.mp3,
    meta: {
        thumbnail: props.song.meta?.thumbnail || null,
        is_youtube: props.song.meta?.is_youtube || false,
        cover: props.song.meta?.cover || false,
        retry: props.song.meta?.retry || false,
        is_smart: props.song.meta?.is_smart || false,
    },
});

function submit() {
    form.put(`/backstage/songs/${props.song.id}`);
}

function deleteSong() {
    if (!confirm('Are you sure you want to delete this song? This action cannot be undone.')) {
        return;
    }

    deleting.value = true;
    router.delete(`/backstage/songs/${props.song.id}`, {
        onFinish: () => {
            deleting.value = false;
        },
    });
}
</script>
